import type { ChangedRange } from "@/features/workspace/lib/diffRangeHighlight"

export interface RewriteSessionContext {
  status: "none" | "running" | "awaiting_human_decision" | "terminal"
  runId: string | null
  hitlPayload: Record<string, unknown> | null
  messages: Array<{ id: string; role: "user" | "assistant"; content: string }>
  terminalReason: string | null
  terminalError: string | null
  /**
   * Latest committed draft/highlight state, read straight off the rewrite
   * checkpoint (persist_commit persists these as real graph-state fields).
   * Lets a page refresh hydrate the canvas without waiting for a live
   * stream event to repopulate it.
   */
  artifactBlobUrl: string | null
  updatedOioDraft: string | null
  draftRevision: number | null
  changedRanges: ChangedRange[] | null
}

const NONE_SESSION: RewriteSessionContext = {
  status: "none",
  runId: null,
  hitlPayload: null,
  messages: [],
  terminalReason: null,
  terminalError: null,
  artifactBlobUrl: null,
  updatedOioDraft: null,
  draftRevision: null,
  changedRanges: null,
}

/**
 * Where the OIO rewrite chat's session routes live (behind the runtime
 * sidecar's raw proxy). Another order type's chat passes its own base — the
 * Order-in-Appeal chat uses `/api/v1/rewrite/oia/session` — and gets the same
 * `…/{threadId}` and `…/{threadId}/cancel` shapes.
 */
export const DEFAULT_REWRITE_SESSION_BASE = "/api/v1/rewrite/session"

/**
 * `headers` are sent only when given (the Order-in-Appeal chat's bearer
 * token); without them the request is exactly the OIO one.
 */
export async function fetchRewriteSession(
  threadId: string,
  basePath: string = DEFAULT_REWRITE_SESSION_BASE,
  headers?: Record<string, string>,
): Promise<RewriteSessionContext> {
  try {
    const url = `${basePath}/${encodeURIComponent(threadId)}`
    const resp = headers ? await fetch(url, { headers }) : await fetch(url)
    if (resp.status === 404) return NONE_SESSION
    if (!resp.ok) return NONE_SESSION
    const session = (await resp.json()) as Partial<RewriteSessionContext>
    return {
      ...NONE_SESSION,
      ...session,
      messages: session.messages ?? [],
    }
  } catch {
    return NONE_SESSION
  }
}

export async function cancelRewriteSession(
  threadId: string,
  basePath: string = DEFAULT_REWRITE_SESSION_BASE,
  headers?: Record<string, string>,
): Promise<void> {
  await fetch(`${basePath}/${encodeURIComponent(threadId)}/cancel`, {
    method: "POST",
    ...(headers ? { headers } : {}),
  })
}
